import React, { useEffect, useMemo, useRef, useState } from "react";
import { ControllerDiagnosticoSearch } from "../../../Service/Service.consulta";
import {
  ControllerEliminarDiagnosticoPaciente,
  GuardarDiagnosticoPaciente,
} from "../../../Service/Service.diagnostico";
import { GetTokenDecoded } from "../../../utils/storeUsuario";
import HCModal from "../HCModal";
import { Autocomplete, Field, Section, Segmented } from "../HCPiezas";

/* ============================================================================
   Agregar diagnóstico — migrado a HCModal.

   Payload y endpoints idénticos: POST /diagnostico_paciente_cita para el alta y
   DELETE /diagnostico?id= para la baja. Los valores de `typo_diagnostico`
   siguen siendo PRESUNTIVOS / DEFINITIVOS.

   Menos clics:
   · El CIE-10 se confirma con Enter sobre el resultado resaltado, en vez de
     abrir un select y elegir.
   · El tipo es un segmented: una pulsación en lugar de abrir-elegir.
   · «Guardar y agregar otro» deja el modal abierto y limpio. Poner tres
     diagnósticos ya no obliga a cerrar y reabrir tres veces.
   · La lista de lo ya agregado en esta consulta se ve AQUÍ, con su papelera:
     antes había que cerrar el modal para saber qué llevabas puesto.
   ========================================================================== */

const TIPOS = [
  { value: "PRESUNTIVOS", label: "Presuntivo" },
  { value: "DEFINITIVOS", label: "Definitivo" },
];

export default function ModalDiagnostico({ show, onHide, cita, paciente, onGuardado }) {
  const userLocal = GetTokenDecoded();
  const [dx, setDx] = useState(null); // { value, label, codigo, nombre }
  const [tipo, setTipo] = useState("PRESUNTIVOS");
  const [comentario, setComentario] = useState("");
  const [loading, setLoading] = useState(false);
  const [intentado, setIntentado] = useState(false);
  const dxRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    setDx(null);
    setTipo("PRESUNTIVOS");
    setComentario("");
    setIntentado(false);
  }, [show]);

  const buscar = async (texto) => {
    const r = await ControllerDiagnosticoSearch(texto);
    if (!r || !r.success) return [];
    return (r.data || []).map((d) => ({
      value: d.id,
      codigo: d.codigo,
      nombre: d.nombre,
      label: `${d.codigo} — ${d.nombre}`,
    }));
  };

  const faltantes = useMemo(() => (dx ? [] : ["CIE-10"]), [dx]);

  const guardar = async (seguirAgregando) => {
    setIntentado(true);
    if (!dx) {
      dxRef.current?.querySelector("input")?.focus();
      return false;
    }
    setLoading(true);
    const r = await GuardarDiagnosticoPaciente({
      iddiagnostico_cita_paciente: 0,
      idcita: cita?.idcita,
      idpaciente: paciente?.idpaciente ?? cita?.pacientes?.idpaciente,
      iddiagnostico: dx.value,
      iddoctor: cita?.doctor?.iddoctor,
      idclinica: userLocal?.idclinica,
      comentario,
      typo_diagnostico: tipo,
    });
    setLoading(false);
    if (!r || r.success !== true) return false;

    // Refresca la consulta para que la lista de abajo (y el rail) se enteren.
    if (onGuardado) await onGuardado();

    if (seguirAgregando) {
      setDx(null);
      setComentario("");
      setIntentado(false);
      return true;
    }
    onHide && onHide();
    return true;
  };

  const quitar = async (id) => {
    const r = await ControllerEliminarDiagnosticoPaciente(id);
    if (r && r.success && onGuardado) await onGuardado();
  };

  const yaAgregados = cita?.diagnostico_cita_paciente || [];
  const subtitulo = [
    paciente?.nombres_completos || cita?.pacientes?.nombres_completos,
    paciente?.edad ? `${paciente.edad} a` : null,
    paciente?.cedula ? `CI ${paciente.cedula}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <HCModal
      isOpen={!!show}
      size="sm"
      title="Agregar diagnóstico"
      subtitle={subtitulo}
      validation={{ missing: faltantes }}
      primaryLabel="Guardar"
      onPrimary={() => guardar(false)}
      primaryLoading={loading}
      secondaryAction={{ label: "Guardar y agregar otro", onClick: () => guardar(true) }}
      onClose={onHide}
      onCancel={onHide}
      hayCambiosSinGuardar={Boolean(dx || comentario)}
    >
      <Section title="Diagnóstico">
        <Field
          label="Diagnóstico CIE-10"
          required
          htmlFor="hc-dx-buscar"
          hint={!dx ? "Busca por código o por descripción. Enter para agregar." : null}
          error={intentado && !dx ? "Selecciona un CIE-10" : null}
        >
          <div ref={dxRef}>
            <Autocomplete
              id="hc-dx-buscar"
              valor={dx}
              onElegir={setDx}
              onBuscar={buscar}
              placeholder="Escribe código o descripción…"
              ariaInvalid={intentado && !dx ? "true" : undefined}
              vacio="Ningún CIE-10 coincide"
              renderOpcion={(op) => (
                <>
                  <span className="hcauto__cod">{op.codigo}</span>
                  <span>{op.nombre}</span>
                </>
              )}
            />
          </div>
        </Field>

        <Field label="Tipo" required>
          <Segmented ariaLabel="Tipo de diagnóstico" options={TIPOS} value={tipo} onChange={setTipo} />
        </Field>

        <Field label="Comentarios" htmlFor="hc-dx-com">
          <textarea
            id="hc-dx-com"
            rows={3}
            placeholder="Observación clínica del diagnóstico (opcional)"
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
          />
        </Field>
      </Section>

      {yaAgregados.length > 0 && (
        <Section title={`Ya agregados en esta consulta · ${yaAgregados.length}`}>
          <div className="hcya">
            {yaAgregados.map((d) => (
              <div className="hcya__fila" key={d.iddiagnostico_cita_paciente}>
                <span className="hcauto__cod">{d.diagnostico?.codigo}</span>
                <span className="hcya__nom">{d.diagnostico?.nombre}</span>
                <span className="hcya__tipo">{(d.typo_diagnostico || "").toLowerCase()}</span>
                <button
                  type="button"
                  className="hcauto__quitar"
                  aria-label={`Quitar ${d.diagnostico?.codigo}`}
                  title="Quitar de la consulta"
                  onClick={() => quitar(d.iddiagnostico_cita_paciente)}
                >
                  🗑
                </button>
              </div>
            ))}
          </div>
        </Section>
      )}
    </HCModal>
  );
}
